import type { Agent, Chat, Feedback, Message } from '@luxuryescapes/contract-support'

type MessageMetaWithAgentPills = NonNullable<Message['meta']> & {
  agentPills?: Array<App.AgentPill>
}

type SupportAssistantMessage = Message & {
  chatId?: string
  meta?: MessageMetaWithAgentPills
}

export function mapChat(result: Chat): App.Chat {
  const chat: App.Chat = {
    agents: result.agents.map<Agent>((agent) => {
      return {
        email: agent.email,
        id: agent.id,
        name: agent.name,
        title: agent.title,
      }
    }),
    agentType: result.agentType,
    createdAt: result.createdAt,
    customerId: result.customerId ?? undefined,
    id: result.id,
    lastMessageAt: result.lastMessageAt ?? undefined,
    status: result.status,
    title: result.title,
    updatedAt: result.updatedAt,
  }

  return chat
}

export function mapChats(result: Array<Chat>): Array<App.Chat> {
  const chats = result.map(mapChat)
  return chats
}

export function mapChatMessage(result: Message): App.ChatMessage {
  const message = result as SupportAssistantMessage
  const meta = message.meta
  const chatMessage: App.ChatMessage = {
    chatId: message.chatId ?? undefined,
    createdAt: result.createdAt ?? new Date().toString(),
    feedback: result.feedback,
    id: result.id as string,
    meta: {
      action: meta?.action as App.ChatActions,
      content: meta?.content?.map((content): App.ChatMessageMetaContent => {
        return {
          text: content.text ?? undefined,
          url: content.url ?? undefined,
          type: content.type ?? undefined,
          category: content.category ?? undefined,
          action: content.action as App.MessageMetaContentAction,
          primary: (content as App.ChatMessageMetaContent).primary ?? undefined,
          validation: {
            regex: content.validation?.regex ?? '',
            message: content.validation?.message ?? '',
          },
        }
      }),
      agentPills: meta?.agentPills?.map((pill) => ({
        text: pill.text,
        value: pill.value ?? pill.text,
        ...(pill.orderId && { orderId: pill.orderId }),
        ...(pill.itemId && { itemId: pill.itemId }),
        ...(pill.roomId && { roomId: pill.roomId }),
      })),
      payload: {
        awaitingDates: meta?.payload?.awaitingDates,
        bookingNumber: meta?.payload?.bookingNumber,
        canBeCancelled: meta?.payload?.canBeCancelled,
        canChangeDates: meta?.payload?.canChangeDates,
        canEditOrder: meta?.payload?.canEditOrder,
        daysBeforeCheckInChangesDisallowed: meta?.payload?.daysBeforeCheckInChangesDisallowed,
        canChangeRooms: meta?.payload?.canChangeRooms,
        chatId: meta?.payload?.chatId,
        checkInDate: meta?.payload?.checkInDate,
        customerId: meta?.payload?.customerId,
        email: meta?.payload?.email,
        firstName: meta?.payload?.firstName,
        id_orders: meta?.payload?.id_orders,
        inputPayload: meta?.payload?.inputPayload,
        itemId: meta?.payload?.itemId,
        roomId: (meta?.payload as App.ChatMessageMetaPayload)?.roomId,
        canChangeGuestName: (meta?.payload as App.ChatMessageMetaPayload)?.canChangeGuestName,
        lastName: meta?.payload?.lastName,
        maskedPhone: meta?.payload?.maskedPhone,
        orderIds: (meta?.payload as App.ChatMessageMetaPayload)?.orderIds,
        orderType: meta?.payload?.orderType,
        genesysAgentCallbackType: meta?.payload?.genesysAgentCallbackType,
        nlsProperties: (meta?.payload as App.ChatMessageMetaPayload)?.nlsProperties?.map((prop) => ({
          offerId: prop.offerId,
          matchLabel: prop.matchLabel,
          description: Array.isArray(prop.description) ? prop.description : undefined,
        })),
      },
    },
    sender: result.sender,
    text: result.text,
    userId: result.userId,

  }

  return chatMessage
}

export function mapChatMessages(result: Chat): Array<App.ChatMessage> {
  return result.messages?.map(mapChatMessage) ?? []
}

export function mapMessageFeedback(result: Feedback): App.ChatMessageFeedback {
  const messageFeedback: App.ChatMessageFeedback = {
    chatId: result.chatId,
    customerId: result.customerId ?? undefined,
    id: result.id,
    issues: result.issues,
    message: result.message ?? undefined,
    messageId: result.messageId,
    sentiment: result.sentiment,
  }

  return messageFeedback
}
